/**
 * @mentions in a note (CRM #7). Wraps an existing <textarea>: typing "@" and a few letters
 * opens a small list of colleagues who can OPEN the account (the server decides —
 * `/work/mentionable`), and picking one inserts "@Name " and remembers who it was.
 *
 *   const m = mentionInput(textarea, { account: () => ({ entity, code }) });
 *   m.getValue()  -> { text, mentions: [user_id, …] }   only people still named in the text
 *   m.reset()
 *
 * The keyboard is Find's walk (estate rule): ↓/↑ move, wrapping; Enter or Tab picks the
 * highlighted one (the first when none is); Escape closes. A sequence guard drops a slow
 * answer for a query the rep has typed past.
 */
import { escapeHtml as esc } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';

export function mentionInput(textarea, { account } = {}) {
  const picked = new Map();            // user id -> the "@Name" inserted for them
  const box = document.createElement('div');
  box.className = 'sc-mention-list';
  box.hidden = true;
  box.setAttribute('role', 'listbox');
  box.style.cssText = 'border:1px solid var(--border-default,#e5e7eb);border-radius:6px;margin-top:4px;'
    + 'background:var(--surface-primary,#fff);max-height:220px;overflow:auto;';
  textarea.insertAdjacentElement('afterend', box);
  const hint = document.createElement('div');
  hint.className = 'sc-list-meta';
  hint.style.marginTop = '4px';
  hint.textContent = 'Type @ to mention a colleague — they will see it on their Today.';
  box.insertAdjacentElement('afterend', hint);

  let users = [];
  let active = -1;
  let seq = 0;
  let timer = null;
  let token = null;                    // {start, query} of the "@…" being typed

  function currentToken() {
    const pos = textarea.selectionStart;
    const before = textarea.value.slice(0, pos);
    const m = before.match(/(^|\s)@([^\s@]{0,40})$/);
    return m ? { start: pos - m[2].length - 1, query: m[2] } : null;
  }

  function close() { box.hidden = true; users = []; active = -1; token = null; }

  function paint() {
    if (!users.length) {
      box.innerHTML = `<div class="sc-list-meta" style="padding:6px 10px">${token && token.query
        ? 'Nobody who can open this account matches that.' : 'Keep typing a name…'}</div>`;
    } else {
      box.innerHTML = users.map((u, i) => `<div class="global-search-result${i === active ? ' is-active' : ''}"
          role="option" data-i="${i}" style="padding:6px 10px">
          <div class="global-search-result-text">
            <div class="global-search-result-title">${esc(u.name)}</div>
            <div class="global-search-result-subtitle">${esc([u.job_title, u.email].filter(Boolean).join(' · '))}</div>
          </div></div>`).join('');
      const el = box.querySelector('.is-active');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
    box.hidden = false;
  }

  async function search(query) {
    const acct = (account && account()) || null;
    if (!acct || !acct.entity) { users = []; token = currentToken(); box.innerHTML = '<div class="sc-list-meta" style="padding:6px 10px">Choose the account first — only people who can open it can be mentioned.</div>'; box.hidden = false; return; }
    const mine = ++seq;
    try {
      const params = { entity: acct.entity, q: query || '' };
      if (acct.code) params.customer = acct.code;
      const res = await salesApi.getMentionable(params);
      if (mine !== seq) return;
      users = ((res.data || res).users) || [];
      active = users.length ? 0 : -1;
      paint();
    } catch {
      if (mine !== seq) return;
      users = [];
      box.innerHTML = '<div class="sc-list-meta" style="padding:6px 10px">Could not look people up just now.</div>';
      box.hidden = false;
    }
  }

  function pick(i) {
    const u = users[i];
    if (!u || !token) return;
    const label = `@${u.name}`;
    const v = textarea.value;
    const end = textarea.selectionStart;
    textarea.value = `${v.slice(0, token.start)}${label} ${v.slice(end)}`;
    const caret = token.start + label.length + 1;
    textarea.setSelectionRange(caret, caret);
    picked.set(u.id, label);
    close();
    textarea.focus();
  }

  textarea.addEventListener('input', () => {
    token = currentToken();
    if (!token) { close(); return; }
    clearTimeout(timer);
    if (!token.query) { users = []; paint(); return; }
    timer = setTimeout(() => search(token.query), 250);
  });

  textarea.addEventListener('keydown', (e) => {
    if (box.hidden) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!users.length) return;
      e.preventDefault();
      active = (active + (e.key === 'ArrowDown' ? 1 : -1) + users.length) % users.length;
      paint();
    } else if ((e.key === 'Enter' || e.key === 'Tab') && users.length) {
      e.preventDefault();
      pick(active >= 0 ? active : 0);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();             // close the list, not the sheet
      close();
    }
  });

  box.addEventListener('mousedown', (e) => {   // mousedown: before the textarea blurs
    const row = e.target.closest('[data-i]');
    if (!row) return;
    e.preventDefault();
    pick(Number(row.dataset.i));
  });
  textarea.addEventListener('blur', () => setTimeout(close, 150));

  return {
    getValue() {
      const text = textarea.value;
      return { text, mentions: [...picked].filter(([, label]) => text.includes(label)).map(([id]) => id) };
    },
    reset() { picked.clear(); close(); },
  };
}
